import type { UploadTransport } from './transport'
import type { SignedPart } from './types'

/**
 * Entrega URLs firmadas de partes bajo demanda, con caché.
 *
 * Por qué no se piden todas de golpe: un archivo de 50 GB son ~1.500 partes.
 * Firmarlas todas produciría una respuesta enorme con URLs que expiran (6 h)
 * antes de llegar a usarse en un enlace peruano lento.
 *
 * Se piden en bloques a medida que el pool de concurrencia las va necesitando,
 * y se pueden refrescar individualmente cuando una caduca a mitad de subida.
 */
export class PartUrlProvider {
  private readonly cache = new Map<number, SignedPart>()

  private uploadId: string | null = null

  private inFlight: Promise<void> | null = null

  constructor(
    private readonly sessionId: string,
    private readonly totalParts: number,
    private readonly transport: UploadTransport,
    private readonly blockSize = 20,
  ) {}

  /** upload_id de R2, disponible tras la primera firma. */
  get multipartUploadId(): string | null {
    return this.uploadId
  }

  async get(partNumber: number): Promise<SignedPart> {
    const cached = this.cache.get(partNumber)
    if (cached) return cached

    // Una sola petición en vuelo: con 4 partes concurrentes pidiendo bloques a
    // la vez, sin esto se dispararían 4 llamadas redundantes.
    while (this.inFlight) {
      await this.inFlight
      const afterWait = this.cache.get(partNumber)
      if (afterWait) return afterWait
    }

    this.inFlight = this.fetchBlockContaining(partNumber)

    try {
      await this.inFlight
    } finally {
      this.inFlight = null
    }

    const part = this.cache.get(partNumber)

    if (!part) {
      throw new Error(`El servidor no devolvió firma para la parte ${partNumber}.`)
    }

    return part
  }

  /**
   * Vuelve a firmar UNA parte concreta.
   *
   * Se llama cuando el almacenamiento responde 403 a mitad de subida, que en
   * una URL prefirmada casi siempre significa expiración y no falta de
   * permisos: el TTL se agotó porque la subida se alargó.
   */
  async refresh(partNumber: number): Promise<string> {
    this.cache.delete(partNumber)

    const response = await this.transport.signParts(this.sessionId, partNumber, partNumber)
    this.uploadId = response.upload_id

    for (const part of response.parts) {
      this.cache.set(part.part_number, part)
    }

    const part = this.cache.get(partNumber)

    if (!part) {
      throw new Error(`No se pudo refirmar la parte ${partNumber}.`)
    }

    return part.url
  }

  private async fetchBlockContaining(partNumber: number): Promise<void> {
    const from = partNumber
    const to = Math.min(partNumber + this.blockSize - 1, this.totalParts)

    const response = await this.transport.signParts(this.sessionId, from, to)
    this.uploadId = response.upload_id

    for (const part of response.parts) {
      this.cache.set(part.part_number, part)
    }
  }
}
